import React from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import {
  NavigationContainer,
  createNavigationContainerRef,
} from '@react-navigation/native';

import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { AccountDeletionAckScreen } from '../screens/auth/AccountDeletionAckScreen';
import { SuspendedAccountScreen } from '../screens/auth/SuspendedAccountScreen';
import { AuthNavigator } from './AuthNavigator';
import { OnboardingNavigator } from './OnboardingNavigator';
import { MainNavigator } from './MainNavigator';
import { ForegroundFcmBanner } from './ForegroundFcmBanner';
import { navigationThemeFor } from './theme';
import type { MainStackParamList } from './types';

const mainNavigationRef = createNavigationContainerRef<MainStackParamList>();

export function RootNavigator() {
  const {
    user,
    loading,
    needsOnboarding,
    profileLoading,
    accountSuspended,
    accountDeletionAckMessage,
    clearAccountDeletionAck,
    reactivateAccount,
    logout,
    expiredAccountPurging,
  } = useAuth();
  const { isDark } = useAppTheme();
  const navTheme = React.useMemo(() => navigationThemeFor(isDark), [isDark]);
  const [mainRouteName, setMainRouteName] = React.useState<string | undefined>();

  if (
    loading ||
    (user && !user.isAnonymous && profileLoading) ||
    (user && !user.isAnonymous && expiredAccountPurging)
  ) {
    return (
 <View className="flex-1 items-center justify-center bg-white dark:bg-black">
        <ActivityIndicator size="large" color="#18181b" />
 <Text className="mt-4 text-sm text-gray-500 dark:text-zinc-400">Yükleniyor…</Text>
      </View>
    );
  }

  if (!user) {
    return (
      <NavigationContainer theme={navTheme}>
        {accountDeletionAckMessage ? (
          <AccountDeletionAckScreen
            message={accountDeletionAckMessage}
            onContinue={clearAccountDeletionAck}
          />
        ) : (
          <AuthNavigator />
        )}
      </NavigationContainer>
    );
  }

  if (!user.isAnonymous && accountSuspended) {
    return (
      <NavigationContainer theme={navTheme}>
        <SuspendedAccountScreen
          onReactivate={reactivateAccount}
          onLogout={logout}
        />
      </NavigationContainer>
    );
  }

  if (needsOnboarding) {
    return (
      <NavigationContainer theme={navTheme}>
        <OnboardingNavigator />
      </NavigationContainer>
    );
  }

  return (
    <NavigationContainer
      ref={mainNavigationRef}
      theme={navTheme}
      onReady={() => {
        setMainRouteName(mainNavigationRef.getCurrentRoute()?.name);
      }}
      onStateChange={() => {
        setMainRouteName(mainNavigationRef.getCurrentRoute()?.name);
      }}>
      <View style={{ flex: 1 }}>
        <ForegroundFcmBanner currentRouteName={mainRouteName} />
        <MainNavigator />
      </View>
    </NavigationContainer>
  );
}
